import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Zap, Globe, ExternalLink, MapPin, X, Send, Check, Loader2, ChevronDown } from 'lucide-react';
import {
  fetchLiveAffiliates,
  selectLiveFeedAffiliates
} from '../../store/slices/liveAffiliatesSlice';

const VERTICALS = ['Casino', 'Sports Betting', 'Poker', 'Lottery', 'Bingo', 'Sweepstake', 'Crypto'];

const COUNTRIES = [
  { code: 'US', name: 'United States' }, { code: 'UK', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' }, { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' }, { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' }, { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' }, { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' }, { code: 'FI', name: 'Finland' },
  { code: 'DK', name: 'Denmark' }, { code: 'PT', name: 'Portugal' },
  { code: 'BR', name: 'Brazil' }, { code: 'MX', name: 'Mexico' },
  { code: 'IN', name: 'India' }, { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' }, { code: 'SG', name: 'Singapore' },
  { code: 'NZ', name: 'New Zealand' }, { code: 'ZA', name: 'South Africa' },
  { code: 'PL', name: 'Poland' }, { code: 'CZ', name: 'Czech Republic' },
  { code: 'AT', name: 'Austria' }, { code: 'CH', name: 'Switzerland' },
  { code: 'BE', name: 'Belgium' }, { code: 'IE', name: 'Ireland' },
  { code: 'RO', name: 'Romania' }, { code: 'HU', name: 'Hungary' },
  { code: 'GR', name: 'Greece' }, { code: 'PH', name: 'Philippines' },
  { code: 'NG', name: 'Nigeria' }, { code: 'KE', name: 'Kenya' },
  { code: 'CL', name: 'Chile' }, { code: 'CO', name: 'Colombia' },
  { code: 'AR', name: 'Argentina' }, { code: 'PE', name: 'Peru' },
  { code: 'TH', name: 'Thailand' }, { code: 'ID', name: 'Indonesia' },
  { code: 'MY', name: 'Malaysia' },
];

const NewAffiliatesLive = () => {
  const dispatch = useDispatch();
  const liveFeedAffiliates = useSelector(selectLiveFeedAffiliates);
  const [animatingId, setAnimatingId] = useState(null);
  const [expandedUrl, setExpandedUrl] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const previousAffiliatesRef = useRef([]);

  // Market request modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    dispatch(fetchLiveAffiliates());
  }, [dispatch]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      dispatch(fetchLiveAffiliates());
    }, 60000);
    return () => clearInterval(refreshInterval);
  }, [dispatch]);

  useEffect(() => {
    if (liveFeedAffiliates.length > 0 && previousAffiliatesRef.current.length > 0) {
      const newAffiliate = liveFeedAffiliates[0];
      const wasInPrevious = previousAffiliatesRef.current.some(
        a => a.affiliate_url === newAffiliate.affiliate_url
      );
      if (!wasInPrevious) {
        setAnimatingId(newAffiliate.affiliate_url);
        setTimeout(() => setAnimatingId(null), 600);
      }
    }
    previousAffiliatesRef.current = liveFeedAffiliates;
  }, [liveFeedAffiliates]);

  const displayAffiliates = liveFeedAffiliates.slice(0, 3);

  const toggleExpand = async (url) => {
    if (expandedUrl === url) {
      setExpandedUrl(null);
      setExpandedData(null);
      return;
    }
    setExpandedUrl(url);
    setExpandedData(null);
    setExpandLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/keyword-affiliate/affiliate-details?affiliate_url=${encodeURIComponent(url)}`, { credentials: 'include' });
      const json = await res.json();
      if (json.success) setExpandedData(json.data);
    } catch (err) {
      console.error('Error fetching affiliate details:', err);
    } finally {
      setExpandLoading(false);
    }
  };

  const getProcessedGeos = (geoData) => {
    if (!geoData) return [];
    return geoData.split(',').map(entry => {
      const [country, count] = entry.split(':');
      const trimmed = country?.trim();
      if (!trimmed || trimmed.toLowerCase() === 'unknown') return null;
      return { country: trimmed, count: count?.trim() || '0' };
    }).filter(Boolean);
  };

  const getUniqueKeywords = (keywordString) => {
    if (!keywordString) return [];
    const seen = new Set();
    return keywordString.split(',').map(k => k.trim()).filter(k => {
      if (!k || seen.has(k.toLowerCase())) return false;
      seen.add(k.toLowerCase());
      return true;
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const toggleVertical = (v) => {
    setSelectedVerticals(prev =>
      prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
    );
  };

  const toggleCountry = (code) => {
    setSelectedCountries(prev =>
      prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]
    );
  };

  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleSubmitRequest = async () => {
    if (selectedVerticals.length === 0 && selectedCountries.length === 0) return;
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/market-request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verticals: selectedVerticals,
          countries: selectedCountries
        })
      });
      const data = await response.json();
      if (data.success) {
        setSubmitStatus('success');
        setTimeout(() => {
          setShowModal(false);
          setSelectedVerticals([]);
          setSelectedCountries([]);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedVerticals([]);
    setSelectedCountries([]);
    setCountrySearch('');
    setSubmitStatus(null);
  };

  return (
    <>
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-5 transition-colors duration-300">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-ping" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300">Newly Added Affiliates</h3>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            >
              <MapPin className="w-3 h-3" />
              Request Specific Market
            </button>
            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Live</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 overflow-hidden">
          {displayAffiliates.map((affiliate) => (
            <div key={affiliate.affiliate_url}>
              <div
                onClick={() => toggleExpand(affiliate.affiliate_url)}
                className={`flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 dark:border-slate-700 transition-all duration-300 hover:bg-blue-50 dark:hover:bg-slate-800 cursor-pointer
                  ${animatingId === affiliate.affiliate_url ? 'animate-slideIn bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700' : ''}
                  ${expandedUrl === affiliate.affiliate_url ? 'bg-blue-50/50 dark:bg-slate-800/50 border-blue-200 dark:border-blue-800/40' : ''}`}
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-700 dark:text-slate-200 text-sm truncate">{affiliate.affiliate_url}</p>
                  {affiliate.top_page && (
                    <a
                      href={affiliate.top_page}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline truncate mt-0.5"
                      title={affiliate.top_page}
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{affiliate.top_page.replace(/^https?:\/\/(www\.)?/, '').substring(0, 50)}{affiliate.top_page.length > 60 ? '...' : ''}</span>
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {affiliate.vertical && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                      {affiliate.vertical}
                    </span>
                  )}
                  {(() => {
                    const countries = (affiliate.country || '')
                      .split(',')
                      .map(c => c.trim().toUpperCase())
                      .filter(c => c && c.toLowerCase() !== 'unknown');
                    return countries.length > 0 ? (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        {countries.join(', ')}
                      </span>
                    ) : null;
                  })()}
                  <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-500 transition-transform duration-200 ${expandedUrl === affiliate.affiliate_url ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {expandedUrl === affiliate.affiliate_url && (
                <div className="mt-1 p-4 space-y-4 rounded-lg bg-gradient-to-b from-gray-50 to-white dark:from-slate-800/50 dark:to-slate-900 border border-gray-100 dark:border-slate-700 animate-expandIn">
                  {expandLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                      <span className="ml-2 text-sm text-gray-400 dark:text-slate-500">Loading details...</span>
                    </div>
                  ) : expandedData ? (
                    <>
                      {/* Stats Row */}
                      <div>
                        <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Domain Stats</h4>
                        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 dark:divide-slate-700">
                            <div className="p-3 text-center">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">Rank</p>
                              <p className="text-base font-bold text-gray-800 dark:text-white mt-1">{expandedData.rank ? formatNumber(expandedData.rank) : '\u2014'}</p>
                            </div>
                            <div className="p-3 text-center">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">Organic Traffic</p>
                              <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 mt-1">{expandedData.organic_traffic ? formatNumber(expandedData.organic_traffic) : '\u2014'}</p>
                            </div>
                            <div className="p-3 text-center">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">Paid Traffic</p>
                              <p className="text-base font-bold text-gray-800 dark:text-white mt-1">{expandedData.adwords_traffic ? formatNumber(expandedData.adwords_traffic) : '\u2014'}</p>
                            </div>
                            <div className="p-3 text-center">
                              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">Total Traffic</p>
                              <p className="text-base font-bold text-gray-800 dark:text-white mt-1">{expandedData.total_traffic ? formatNumber(expandedData.total_traffic) : '\u2014'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Traffic by Country */}
                      {expandedData.top_geo_organic_traffic && (
                        <div>
                          <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Traffic by Country</h4>
                          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                            <div className="grid grid-cols-1 sm:grid-cols-3">
                              {getProcessedGeos(expandedData.top_geo_organic_traffic).slice(0, 9).map((geo, index) => (
                                <div key={index} className="flex items-center justify-between p-3 border-b border-r border-gray-50 dark:border-slate-700 last:border-r-0">
                                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300 truncate">{geo.country}</span>
                                  <span className="text-xs font-semibold text-gray-900 dark:text-white ml-2">{formatNumber(parseInt(geo.count) || 0)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Two Column: Top Pages + Keywords */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Top Pages */}
                        <div>
                          <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Top Pages</h4>
                          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                            {expandedData.top_page ? (
                              <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-40 overflow-y-auto">
                                {expandedData.top_page.split(',').map(u => u.trim()).filter(Boolean).slice(0, 5).map((page, i) => (
                                  <a key={i} href={page.startsWith('http') ? page : `https://${page}`} target="_blank" rel="noopener noreferrer"
                                     className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-750 transition-colors group">
                                    <span className="w-5 h-5 rounded-md bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-[10px] font-medium text-gray-500 dark:text-slate-400 flex-shrink-0">{i + 1}</span>
                                    <span className="flex-1 min-w-0 text-xs text-gray-600 dark:text-slate-400 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                      {page.replace(/^https?:\/\/(www\.)?/, '')}
                                    </span>
                                    <ExternalLink className="w-3 h-3 text-gray-300 dark:text-slate-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <div className="px-3 py-4 text-center text-xs text-gray-400 dark:text-slate-500">No page data</div>
                            )}
                          </div>
                        </div>

                        {/* Top Keywords */}
                        <div>
                          <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Top Keywords</h4>
                          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                            {expandedData.keywords ? (
                              <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-40 overflow-y-auto">
                                {getUniqueKeywords(expandedData.keywords).slice(0, 8).map((kw, i) => (
                                  <div key={i} className="flex items-center gap-2 px-3 py-2.5">
                                    <span className="w-5 h-5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-[10px] font-medium text-emerald-600 dark:text-emerald-400 flex-shrink-0">{i + 1}</span>
                                    <span className="text-xs text-gray-700 dark:text-slate-300 truncate">{kw}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="px-3 py-4 text-center text-xs text-gray-400 dark:text-slate-500">No keywords available</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Paid Keywords */}
                      {expandedData.paid_keyword && (
                        <div>
                          <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Paid Keywords</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {getUniqueKeywords(expandedData.paid_keyword).slice(0, 10).map((kw, i) => (
                              <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg text-xs text-purple-700 dark:text-purple-300">
                                {kw}
                                <span className="text-[10px] text-purple-400 dark:text-purple-500">paid</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4 text-xs text-gray-400 dark:text-slate-500">
                      No detailed data available for this affiliate
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {displayAffiliates.length === 0 && (
            <div className="text-center py-4 text-gray-400 dark:text-slate-500 text-sm">
              Looking for new affiliates...
            </div>
          )}
        </div>

        <style>{`
          @keyframes slideInLeft {
            0% { opacity: 0; transform: translateX(-20px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          .animate-slideIn { animation: slideInLeft 0.4s ease-out; }
          @keyframes expandIn {
            0% { opacity: 0; transform: translateY(-4px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-expandIn { animation: expandIn 0.2s ease-out; }
        `}</style>
      </div>

      {/* Market Request Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-xl w-full max-w-md max-h-[85vh] flex flex-col animate-modalIn">

            {/* Header */}
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                    <MapPin className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Request Specific Market</h2>
                    <p className="text-xs text-gray-400 dark:text-slate-500">Select verticals & countries</p>
                  </div>
                </div>
                <button onClick={closeModal} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 pb-4 overflow-y-auto flex-1 space-y-4">
              {/* Verticals */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2 uppercase tracking-wider">Verticals</p>
                <div className="flex flex-wrap gap-1.5">
                  {VERTICALS.map((v) => {
                    const selected = selectedVerticals.includes(v);
                    return (
                      <button
                        key={v}
                        onClick={() => toggleVertical(v)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selected
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        {selected && <span className="mr-1">&#10003;</span>}
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 dark:border-slate-800" />

              {/* Countries */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2 uppercase tracking-wider">Countries</p>
                <div className="relative mb-2">
                  <input
                    type="text"
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder="Search countries..."
                    className="w-full pl-3 pr-8 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-xs bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {countrySearch && (
                    <button onClick={() => setCountrySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-1 max-h-36 overflow-y-auto pr-1">
                  {filteredCountries.map((c) => {
                    const selected = selectedCountries.includes(c.code);
                    return (
                      <button
                        key={c.code}
                        onClick={() => toggleCountry(c.code)}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-all text-left ${
                          selected
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        {selected ? (
                          <span className="w-4 h-4 rounded bg-blue-600 flex items-center justify-center flex-shrink-0">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </span>
                        ) : (
                          <span className="w-4 h-4 rounded border border-gray-300 dark:border-slate-600 flex-shrink-0" />
                        )}
                        <span className="truncate">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected chips */}
              {(selectedVerticals.length > 0 || selectedCountries.length > 0) && (
                <div>
                  <div className="border-t border-gray-100 dark:border-slate-800 mb-3" />
                  <div className="flex flex-wrap gap-1.5">
                    {selectedVerticals.map((v) => (
                      <span key={v} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        {v}
                        <button onClick={() => toggleVertical(v)} className="p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800/50">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {selectedCountries.map((code) => {
                      const c = COUNTRIES.find(x => x.code === code);
                      return (
                        <span key={code} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                          {c?.name || code}
                          <button onClick={() => toggleCountry(code)} className="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3.5 border-t border-gray-100 dark:border-slate-800 flex items-center justify-end gap-2">
              {submitStatus === 'success' ? (
                <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <Check className="w-4 h-4" />
                  Request submitted!
                </div>
              ) : submitStatus === 'error' ? (
                <>
                  <span className="text-xs text-red-500 mr-2">Failed to submit</span>
                  <button
                    onClick={handleSubmitRequest}
                    className="px-4 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="px-3.5 py-2 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitRequest}
                    disabled={submitting || (selectedVerticals.length === 0 && selectedCountries.length === 0)}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 dark:disabled:bg-slate-700 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    {submitting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    {submitting ? 'Sending...' : 'Submit Request'}
                  </button>
                </>
              )}
            </div>
          </div>

          <style>{`
            @keyframes modalIn {
              0% { opacity: 0; transform: scale(0.95) translateY(10px); }
              100% { opacity: 1; transform: scale(1) translateY(0); }
            }
            .animate-modalIn { animation: modalIn 0.2s ease-out; }
          `}</style>
        </div>
      )}
    </>
  );
};

export default NewAffiliatesLive;
